import i18next from 'i18next';

import {
    createTrackMutedEvent
} from '../../analytics/AnalyticsEvents';
import { sendAnalytics } from '../../analytics/functions';
import { showErrorNotification, showNotification } from '../../notifications/actions';
import { getCurrentConference } from '../conference/functions';
import { MHTrackErrors, MHTrackEvents } from '../lib-meet-hour';
import {
    setAudioMuted,
    setScreenshareMuted,
    setVideoMuted
} from '../media/actions';
import {
    CAMERA_FACING_MODE,
    MEDIA_TYPE,
    VIDEO_MUTISM_AUTHORITY,
    VIDEO_TYPE
} from '../media/constants';
import { getLocalParticipant } from '../participants/functions';

import {
    SET_NO_SRC_DATA_NOTIFICATION_UID,
    TRACK_ADDED,
    TRACK_CREATE_CANCELED,
    TRACK_CREATE_ERROR,
    TRACK_MUTE_UNMUTE_FAILED,
    TRACK_NO_DATA_FROM_SOURCE,
    TRACK_REMOVED,
    TRACK_STOPPED,
    TRACK_UPDATED,
    TRACK_UPDATE_LAST_VIDEO_MEDIA_EVENT,
    TRACK_WILL_CREATE
} from './actionTypes';
import {
    getLocalDesktopTrack,
    getLocalTrack,
    getLocalTracks,
    getTrackByMHTrack
} from './functions';
import logger from './logger';
import {
    createLocalTracksF
} from './mhFunctions';

/**
 * Requests the creating of the desired media type tracks. Desire is expressed
 * by base/media unless the function caller specifies desired media types
 * explicitly and thus override base/media. Dispatches a
 * {@code createLocalTracksA} action for the desired media types for which there
 * are no existing tracks yet.
 *
 * @returns {Function}
 */
export function createDesiredLocalTracks(...desiredTypes) {
    return (dispatch, getState) => {
        const state = getState();

        dispatch(destroyLocalDesktopTrackIfExists());

        if (desiredTypes.length === 0) {
            const { audio, video } = state['features/base/media'];

            audio.muted || desiredTypes.push(MEDIA_TYPE.AUDIO);

            // XXX When the app is coming into the foreground from the
            // background in order to handle a URL, it may realize the new
            // background state soon after it has tried to create the local
            // tracks requested by the URL. Ignore
            // VIDEO_MUTISM_AUTHORITY.BACKGROUND and create the local video
            // track if no other VIDEO_MUTISM_AUTHORITY has muted it. The local
            // video track will be muted until the app realizes the new
            // background state.

            // eslint-disable-next-line no-bitwise
            (video.muted & ~VIDEO_MUTISM_AUTHORITY.BACKGROUND)
                || desiredTypes.push(MEDIA_TYPE.VIDEO);
        }

        const availableTypes
            = getLocalTracks(
                    state['features/base/tracks'],
                    /* includePending */ true)
                .map(t => t.mediaType);

        // We need to create the desired tracks which are not already available.
        const createTypes
            = desiredTypes.filter(type => availableTypes.indexOf(type) === -1);

        createTypes.length
            && dispatch(createLocalTracksA({ devices: createTypes }));
    };
}

/**
 * Request to start capturing local audio and/or video. By default, the user
 * facing camera will be selected.
 *
 * @param {Object} [options] - For info @see MeetHourJS.createLocalTracks.
 * @returns {Function}
 */
export function createLocalTracksA(options = {}) {
    return (dispatch, getState) => {
        const devices
            = options.devices || [ MEDIA_TYPE.AUDIO, MEDIA_TYPE.VIDEO ];
        const store = {
            dispatch,
            getState
        };

        // Collected so that a Promise can be returned below. The thunk used to
        // return undefined, which made the documented `dispatch(createLocalTracksA())
        // .then(...)` usage throw a TypeError instead of waiting for gUM.
        const gumProcesses = [];

        // The following executes on React Native only at the time of this
        // writing. The effort to port Web's createInitialLocalTracksAndConnect
        // is significant and that's where the function createLocalTracksF got
        // born. I started with the idea a porting so that we could inherit the
        // ability to getUserMedia for audio only or video only if getUserMedia
        // for audio and video fails. Eventually though, I realized that on
        // mobile we do not have combined permission prompts implemented anyway
        // (either because there are no such prompts or it does not make sense
        // to implement them) and the right thing to do is to ask for each
        // device separately.
        for (const device of devices) {
            if (getLocalTrack(
                    getState()['features/base/tracks'],
                    device,
                    /* includePending */ true)) {
                throw new Error(`Local track for ${device} already exists`);
            }

            const gumProcess
                = createLocalTracksF(
                    {
                        cameraDeviceId: options.cameraDeviceId,
                        devices: [ device ],
                        facingMode:
                            options.facingMode || CAMERA_FACING_MODE.USER,
                        micDeviceId: options.micDeviceId
                    },
                    store)
                .then(
                    localTracks => {
                        // Because GUM is called for 1 device (which is actually
                        // a media type 'audio', 'video', 'screen', etc.) we
                        // should not get more than one MHTrack.
                        if (localTracks.length !== 1) {
                            throw new Error(
                                `Expected exactly 1 track, but was given ${
                                    localTracks.length} tracks for device: ${
                                    device}.`);
                        }

                        if (gumProcess.canceled) {
                            return _disposeTracks(localTracks)
                                .then(() =>
                                    dispatch(_trackCreateCanceled(device)));
                        }

                        return dispatch(trackAdded(localTracks[0]));
                    },
                    reason =>
                        dispatch(
                            gumProcess.canceled
                                ? _trackCreateCanceled(device)
                                : _onCreateLocalTracksRejected(
                                    reason,
                                    device)));

            /**
             * Cancels the {@code getUserMedia} process represented by this
             * {@code Promise}.
             *
             * @returns {Promise} This {@code Promise} i.e. {@code gumProcess}.
             */
            gumProcess.cancel = () => {
                gumProcess.canceled = true;

                return gumProcess;
            };

            gumProcesses.push(gumProcess);

            dispatch({
                type: TRACK_WILL_CREATE,
                track: {
                    gumProcess,
                    local: true,
                    mediaType: device
                }
            });
        }

        return Promise.all(gumProcesses);
    };
}

/**
 * Calls MHLocalTrack#dispose() on all local tracks ignoring errors when
 * track is already disposed. After that signals tracks to be removed.
 *
 * @returns {Function}
 */
export function destroyLocalTracks() {
    // The promise is returned so that callers can actually await the teardown.
    // It used to be dropped on the floor, which let a subsequent conference join
    // read half-torn-down tracks out of the store.
    return (dispatch, getState) =>

        // First wait until any getUserMedia in progress is settled and then get
        // rid of all local tracks.
        _cancelGUMProcesses(getState)
            .then(() =>
                dispatch(
                    _disposeAndRemoveTracks(
                        getState()['features/base/tracks']
                            .filter(t => t.local)
                            .map(t => t.mhTrack))))

            // The tracks are out of the store by this point, so a failure to
            // release the underlying device must not abort the caller's chain
            // (e.g. the conference re-init that follows a visitor redirect).
            .catch(err => logger.error('Failed to destroy local tracks', err));
}

/**
 * Signals that the passed MHLocalTrack has triggered a no data from source event.
 *
 * @param {MHLocalTrack} track - The track.
 * @returns {{
*     type: TRACK_NO_DATA_FROM_SOURCE,
*     track: Track
* }}
*/
export function noDataFromSource(track) {
    return {
        type: TRACK_NO_DATA_FROM_SOURCE,
        track
    };
}

/**
 * Displays a no data from source video error if needed.
 *
 * @param {MHLocalTrack} mhTrack - The track.
 * @returns {Function}
 */
export function showNoDataFromSourceVideoError(mhTrack) {
    return (dispatch, getState) => {
        let notificationInfo;

        const track = getTrackByMHTrack(getState()['features/base/tracks'], mhTrack);

        if (!track) {
            return;
        }

        if (track.isReceivingData) {
            notificationInfo = undefined;
        } else {
            const notificationAction = showErrorNotification({
                descriptionKey: i18next.t('dialog.cameraNotSendingData'),
                titleKey: i18next.t('dialog.cameraNotSendingDataTitle')
            });

            dispatch(notificationAction);
            notificationInfo = {
                uid: notificationAction.uid
            };
        }
        dispatch(trackNoDataFromSourceNotificationInfoChanged(mhTrack, notificationInfo));
    };
}

// /**
//  * Signals that the local participant is ending screensharing or beginning the
//  * screensharing flow.
//  *
//  * @param {boolean} enabled - The state to toggle screen sharing to.
//  * @returns {{
//  *     type: TOGGLE_SCREENSHARING,
//  *      on: boolean,
//  * }}
//  */
// export function toggleScreensharing(enabled) {

//     return {
//         type: TOGGLE_SCREENSHARING,
//         enabled
//     };
// }


/**
 * Replaces one track with another for one renegotiation instead of invoking
 * two renegotiations with a separate removeTrack and addTrack. Disposes the
 * removed track as well.
 *
 * @param {MHLocalTrack|null} oldTrack - The track to dispose.
 * @param {MHLocalTrack|null} newTrack - The track to use instead.
 * @param {MHConference} [conference] - The conference from which to remove
 * and add the tracks. If one is not provided, the conference in the redux store
 * will be used.
 * @returns {Function}
 */
export function replaceLocalTrack(oldTrack, newTrack, conference) {
    return async (dispatch, getState) => {

        const resolvedConference = conference
            || getState()['features/base/conference'].conference;

        // Diagnostic: when useAudioStream/useVideoStream is called before the conference
        // exists (hangup -> rejoin inside the same page load, where the module-level
        // _replaceLocalAudioTrackQueue may drain a queued task that was enqueued against
        // a torn-down conference), the track would be replaced in redux state but never
        // added to the new conference's MHConference - the local mic/cam indicator goes
        // unmuted but remote participants receive nothing because the sender was never
        // attached. Log so we can tell this race from a successful path.
        if (!resolvedConference) {
            logger.warn(
                '[DIAG] replaceLocalTrack called with no conference in redux; newTrack'
                + `=${newTrack?.getType?.()} ${newTrack?.getId?.()} will be queued via redux`
                + ' and added on the next TRACK_ADDED dispatch that lands a conference.');
        }

        if (resolvedConference) {
            try {
                // If oldTrack is null or disposed, just add the newTrack instead of replacing
                if (oldTrack) {
                    await resolvedConference.replaceTrack(oldTrack, newTrack)
                        .catch(error => {
                            // Track might be already disposed because of async device switching.
                            // Ignore this known race and continue with store cleanup.
                            // Also handle the case where the transceiver is missing/null.
                            // The fallback is only ever valid for video, which is the one media type a
                            // conference accepts more than one local source of. Running it for audio could
                            // only ever produce 'Cannot add second audio track to the conference' - the
                            // failed replace turned into an add that the conference rejects by design, and
                            // the user was shown that as a microphone failure. For audio the original error
                            // is surfaced instead.
                            // Adding only helps when it was the old track that was gone. A new track that is
                            // itself disposed cannot be added either; trying would just fail a second time.
                            const canAddAsNewSource = (newTrack?.isVideoTrack?.() ?? false) && !newTrack.disposed;

                            if (canAddAsNewSource
                                && (error.name === MHTrackErrors.TRACK_IS_DISPOSED
                                    || error.message?.includes?.('no transceiver'))) {
                                logger.warn('replaceLocalTrack: falling back to addTrack', {
                                    reason: error.name || error.message
                                });

                                // Fallback: add the newTrack instead of replacing
                                return resolvedConference.addTrack(newTrack);
                            }

                            throw error;
                        });
                } else if (newTrack) {
                    // No oldTrack to replace, just add the newTrack. A track disposed before it reaches the
                    // conference is a race, not a fault - the caller's track is simply gone. It is still
                    // reported to the caller (swallowing it would leave the caller holding a dead track as
                    // its current one), just not logged as an error.
                    await resolvedConference.addTrack(newTrack).catch(error => {
                        error.name === MHTrackErrors.TRACK_IS_DISPOSED
                            ? logger.warn(`Not adding an already disposed local track ${newTrack}`)
                            : logger.error('Failed to add new track', error);

                        throw error;
                    });
                }
            } catch (error) {
                // Every attempt above - the replace, and its own addTrack fallback - has failed. Without this,
                // `dispatch(replaceStoredTracks(...))` below is never reached (the throw exits the function
                // here), which leaves two things broken for the rest of the call:
                //
                //  1. `newTrack` (the mic/camera/screenshare capture just obtained via getUserMedia /
                //     getDisplayMedia) never gets attached to anything and never gets disposed. The hardware
                //     capture keeps running regardless - dispose() is what calls stopStream() - so the OS/
                //     browser can keep reporting the device as in use, which is exactly what turns the next
                //     retry into a DEVICE_BUSY/NotReadableError instead of a normal getUserMedia() call. This
                //     is the "camera won't come back, mic won't come back" cascade after one failed replace.
                //  2. Redux's `features/base/tracks` never updates, so it goes on describing `oldTrack` as
                //     the live mic/camera/screenshare even though the conference could not find anywhere to
                //     put it - the mute/unmute button keeps acting on a track the conference has effectively
                //     already lost, which can never succeed until the page is reloaded.
                //
                // Neither cleanup step below is allowed to throw past this catch - a failure while cleaning
                // up after a failure is exactly how the original bug compounds into an unrecoverable state.
                logger.error(
                    `replaceLocalTrack: giving up on ${oldTrack} -> ${newTrack}; resetting local state so`
                    + ' the user can retry instead of being left with a stuck mic/camera/screenshare.',
                    error);

                if (newTrack && !newTrack.disposed) {
                    try {
                        await newTrack.dispose();
                    } catch (disposeError) {
                        logger.warn(`replaceLocalTrack: failed to dispose orphaned ${newTrack}`, disposeError);
                    }
                }

                // Clearing the old track disposes it. That is right when the conference has lost it, but a
                // replace can also fail with the old track untouched - the new track was already disposed,
                // which the conference checks before changing anything. Disposing it then turned off the
                // camera or mic that was still live on the call.
                const oldTrackStillOnConference = Boolean(oldTrack)
                    && !oldTrack.disposed
                    && resolvedConference.getLocalTracks().includes(oldTrack);

                try {
                    if (!oldTrackStillOnConference) {
                        await dispatch(replaceStoredTracks(oldTrack, null));
                    }
                } catch (resetError) {
                    logger.warn(`replaceLocalTrack: failed to clear ${oldTrack} from the store after the`
                        + ' failed replace', resetError);
                }

                throw error;
            }
        }

        return dispatch(replaceStoredTracks(oldTrack, newTrack));
    };
}

/**
 * Replaces a stored track with another.
 *
 * @param {MHLocalTrack|null} oldTrack - The track to dispose.
 * @param {MHLocalTrack|null} newTrack - The track to use instead.
 * @returns {Function}
 */
function replaceStoredTracks(oldTrack, newTrack) {
    return async dispatch => {
        // We call dispose after doing the replace because dispose will
        // try and do a new o/a after the track removes itself. Doing it
        // after means the MHLocalTrack.conference is already
        // cleared, so it won't try and do the o/a.
        if (oldTrack) {
            await dispatch(_disposeAndRemoveTracks([ oldTrack ]));
        }

        if (newTrack) {
            // The mute state of the new track should be reflected in the app's mute state. For example, if the
            // app is currently muted and changing to a new track that is not muted, the app's mute state
            // should be falsey. As such, emit a mute event here to set up the app to reflect the track's mute
            // state. If this is not done, the current mute state of the app will be reflected on the track,
            // not vice-versa.
            const setMuted = newTrack.isVideoTrack()
                ? newTrack.getVideoType() === VIDEO_TYPE.DESKTOP
                    ? setScreenshareMuted
                    : setVideoMuted
                : setAudioMuted;
            const isMuted = newTrack.isMuted();

            sendAnalytics(createTrackMutedEvent(newTrack.getType(), 'track.replaced', isMuted));
            logger.log(`Replace ${newTrack.getType()} track - ${isMuted ? 'muted' : 'unmuted'}`);

            await dispatch(setMuted(isMuted));
            await dispatch(_addTracks([ newTrack ]));
        }
    };
}

/**
 * Create an action for when a new track has been signaled to be added to the
 * conference.
 *
 * @param {(MHLocalTrack|MHRemoteTrack)} track - MHTrack instance.
 * @returns {{ type: TRACK_ADDED, track: Track }}
 */
export function trackAdded(track) {
    return (dispatch, getState) => {
        track.on(
            MHTrackEvents.TRACK_MUTE_CHANGED,
            () => dispatch(trackMutedChanged(track)));
        track.on(
            MHTrackEvents.TRACK_VIDEOTYPE_CHANGED,
            type => dispatch(trackVideoTypeChanged(track, type)));

        // participantId
        const local = track.isLocal();
        const mediaType = track.videoType === VIDEO_TYPE.DESKTOP
            ? MEDIA_TYPE.SCREENSHARE
            : track.getType();
        let isReceivingData, noDataFromSourceNotificationInfo, participantId;

        if (local) {
            // Reset the no data from src notification state when we change the track, as it's context is set
            // on a per device basis.
            dispatch(setNoSrcDataNotificationUid());
            const participant = getLocalParticipant(getState);

            if (participant) {
                participantId = participant.id;
            }

            isReceivingData = track.isReceivingData();
            track.on(MHTrackEvents.NO_DATA_FROM_SOURCE, () => dispatch(noDataFromSource({ mhTrack: track })));
            if (!isReceivingData) {
                if (mediaType === MEDIA_TYPE.AUDIO) {
                    const notificationAction = showNotification({
                        descriptionKey: 'dialog.micNotSendingData',
                        titleKey: 'dialog.micNotSendingDataTitle'
                    });

                    dispatch(notificationAction);

                    // Set the notification ID so that other parts of the application know that this was
                    // displayed in the context of the current device.
                    // I.E. The no-audio-signal notification shouldn't be displayed if this was already shown.
                    dispatch(setNoSrcDataNotificationUid(notificationAction.uid));

                    noDataFromSourceNotificationInfo = { uid: notificationAction.uid };
                } else {
                    const timeout = setTimeout(() => dispatch(showNoDataFromSourceVideoError(track)), 5000);

                    noDataFromSourceNotificationInfo = { timeout };
                }

            }

            track.on(MHTrackEvents.LOCAL_TRACK_STOPPED,
                () => dispatch({
                    type: TRACK_STOPPED,
                    track: {
                        mhTrack: track
                    }
                }));
        } else {
            participantId = track.getParticipantId();
            isReceivingData = true;
        }

        if (track && track?.videoType === 'desktop' && local) {
            const conference = getCurrentConference(getState);

            conference.sendCommand('desktopTrackMuted', {
                attributes: {
                    id: participantId,
                    muted: track.isMuted(),
                    videoType: 'desktop'
                }
            });
        }

        // Respect localFlipX setting when computing mirror for local video tracks
        const shouldMirror = _shouldMirror(track);
        const { localFlipX } = getState()['features/base/settings'];
        const mirror = shouldMirror && localFlipX !== false;

        return dispatch({
            type: TRACK_ADDED,
            track: {
                mhTrack: track,
                isReceivingData,
                local,
                mediaType,
                mirror,
                muted: track.isMuted(),
                noDataFromSourceNotificationInfo,
                participantId,
                videoStarted: false,
                videoType: track.videoType
            }
        });
    };
}

/**
 * Create an action for when a track's muted state has been signaled to be
 * changed.
 *
 * @param {(MHLocalTrack|MHRemoteTrack)} track - MHTrack instance.
 * @returns {{
 *     type: TRACK_UPDATED,
 *     track: Track,
 *     muted: boolean
 * }}
 */
export function trackMutedChanged(track) {

    return {
        type: TRACK_UPDATED,
        track: {
            mhTrack: track,
            muted: track.isMuted()
        }
    };
}

/**
 * Create an action for when a track's no data from source notification information changes.
 *
 * @param {MHLocalTrack} track - MHTrack instance.
 * @param {Object} noDataFromSourceNotificationInfo - Information about no data from source notification.
 * @returns {{
 *     type: TRACK_UPDATED,
 *     track: Track
 * }}
 */
export function trackNoDataFromSourceNotificationInfoChanged(track, noDataFromSourceNotificationInfo) {
    return {
        type: TRACK_UPDATED,
        track: {
            mhTrack: track,
            noDataFromSourceNotificationInfo
        }
    };
}

/**
 * Create an action for when a track has been signaled for removal from the
 * conference.
 *
 * @param {(MHLocalTrack|MHRemoteTrack)} track - MHTrack instance.
 * @returns {{
 *     type: TRACK_REMOVED,
 *     track: Track
 * }}
 */
export function trackRemoved(track) {
    track.removeAllListeners(MHTrackEvents.TRACK_MUTE_CHANGED);
    track.removeAllListeners(MHTrackEvents.TRACK_VIDEOTYPE_CHANGED);
    track.removeAllListeners(MHTrackEvents.NO_DATA_FROM_SOURCE);

    return {
        type: TRACK_REMOVED,
        track: {
            mhTrack: track
        }
    };
}

/**
 * Signal that track's video started to play.
 *
 * @param {(MHLocalTrack|MHRemoteTrack)} track - MHTrack instance.
 * @returns {{
 *     type: TRACK_UPDATED,
 *     track: Track
 * }}
 */
export function trackVideoStarted(track) {
    return {
        type: TRACK_UPDATED,
        track: {
            mhTrack: track,
            videoStarted: true
        }
    };
}

/**
 * Create an action for when participant video type changes.
 *
 * @param {(MHLocalTrack|MHRemoteTrack)} track - MHTrack instance.
 * @param {VIDEO_TYPE|undefined} videoType - Video type.
 * @returns {{
 *     type: TRACK_UPDATED,
 *     track: Track
 * }}
 */
export function trackVideoTypeChanged(track, videoType) {
    const mediaType = videoType === VIDEO_TYPE.CAMERA ? MEDIA_TYPE.VIDEO : MEDIA_TYPE.SCREENSHARE;

    return {
        type: TRACK_UPDATED,
        track: {
            mhTrack: track,
            videoType,
            mediaType
        }
    };
}

/**
 * Signals passed tracks to be added.
 *
 * @param {(MHLocalTrack|MHRemoteTrack)[]} tracks - List of tracks.
 * @private
 * @returns {Function}
 */
function _addTracks(tracks) {
    return dispatch => Promise.all(tracks.map(t => dispatch(trackAdded(t))));
}

/**
 * Cancels and waits for any {@code getUserMedia} process/currently in progress
 * to complete/settle.
 *
 * @param {Function} getState - The redux store {@code getState} function used
 * to obtain the state.
 * @private
 * @returns {Promise} - A {@code Promise} resolved once all
 * {@code gumProcess.cancel()} {@code Promise}s are settled because all we care
 * about here is to be sure that the {@code getUserMedia} callbacks have
 * completed (i.e. Returned from the native side).
 */
function _cancelGUMProcesses(getState) {
    const logError
        = error =>
            logger.error('gumProcess.cancel failed', JSON.stringify(error));

    return Promise.all(
        getState()['features/base/tracks']
            .filter(t => t.local)
            .map(({ gumProcess }) =>
                gumProcess && gumProcess.cancel().catch(logError)));
}

/**
 * Signals passed tracks to be removed and disposes them afterwards.
 *
 * The order matters. {@link MHLocalTrack#dispose} sets the track's `disposed`
 * flag last, only after it has awaited {@code conference.removeTrack()}, so
 * disposing before the removal is signalled leaves a window in which redux still
 * advertises a track that lib-meet-hour already rejects with
 * `track.track_is_disposed` (see {@link _addLocalTracksToConference}). Removing
 * first also means a failing dispose() can no longer strand the track in the
 * store forever.
 *
 * Removal from the conference itself is deliberately left alone: the
 * {@code TRACK_REMOVED} payload carries no `local` flag, so it never reaches the
 * base/conference middleware's track sync, and dispose() stays the thing that
 * calls {@code conference.removeTrack()}. Reordering here therefore only moves
 * the store update earlier -- it does not change the offer/answer sequence.
 *
 * @param {(MHLocalTrack|MHRemoteTrack)[]} tracks - List of tracks.
 * @protected
 * @returns {Function}
 */
export function _disposeAndRemoveTracks(tracks) {
    return dispatch =>
        Promise.all(tracks.map(t => dispatch(trackRemoved(t))))
            .then(() => _disposeTracks(tracks));
}

/**
 * Disposes passed tracks.
 *
 * @param {(MHLocalTrack|MHRemoteTrack)[]} tracks - List of tracks.
 * @private
 * @returns {Promise} - A Promise resolved once {@link MHTrack.dispose()} is
 * done for every track from the list.
 */
function _disposeTracks(tracks) {
    return Promise.all(
        tracks.map(t =>
            t.dispose()
                .catch(err => {
                    // Track might be already disposed so ignore such an error.
                    // Of course, re-throw any other error(s).
                    if (err.name !== MHTrackErrors.TRACK_IS_DISPOSED) {
                        throw err;
                    }
                })));
}

/**
 * Implements the {@code Promise} rejection handler of
 * {@code createLocalTracksA} and {@code createLocalTracksF}.
 *
 * @param {Object} error - The {@code Promise} rejection reason.
 * @param {string} device - The device/{@code MEDIA_TYPE} associated with the
 * rejection.
 * @private
 * @returns {Function}
 */
function _onCreateLocalTracksRejected(error, device) {
    return dispatch => {
        // If permissions are not allowed, alert the user.
        dispatch({
            type: TRACK_CREATE_ERROR,
            permissionDenied: error?.name === 'SecurityError',
            trackType: device
        });
    };
}

/**
 * Returns true if the provided {@code MHTrack} should be rendered as a
 * mirror.
 *
 * We only want to show a video in mirrored mode when:
 * 1) The video source is local, and not remote.
 * 2) The video source is a camera, not a desktop (capture).
 * 3) The camera is capturing the user, not the environment.
 *
 * TODO Similar functionality is part of lib-meet-hour. This function should be
 * removed after https://github.com/jitsi/lib-meet-hour/pull/187 is merged.
 *
 * @param {(MHLocalTrack|MHRemoteTrack)} track - MHTrack instance.
 * @private
 * @returns {boolean}
 */
function _shouldMirror(track) {
    return (
        track
            && track.isLocal()
            && track.isVideoTrack()
            && track.getVideoType() !== VIDEO_TYPE.DESKTOP
            && track.mediaType !== MEDIA_TYPE.SCREENSHARE

            // XXX The type of the return value of MHLocalTrack's
            // getCameraFacingMode happens to be named CAMERA_FACING_MODE as
            // well, it's defined by lib-meet-hour. Note though that the type
            // of the value on the right side of the equality check is defined
            // by meet-hour. The type definitions are surely compatible today
            // but that may not be the case tomorrow.
            && track.getCameraFacingMode() === CAMERA_FACING_MODE.USER);
}

/**
 * Signals that track create operation for given media track has been canceled.
 * Will clean up local track stub from the redux state which holds the
 * {@code gumProcess} reference.
 *
 * @param {MEDIA_TYPE} mediaType - The type of the media for which the track was
 * being created.
 * @private
 * @returns {{
 *     type,
 *     trackType: MEDIA_TYPE
 * }}
 */
function _trackCreateCanceled(mediaType) {
    return {
        type: TRACK_CREATE_CANCELED,
        trackType: mediaType
    };
}

/**
 * If a local desktop track exists (muted or live), it calls
 * {@link _disposeAndRemoveTracks} on it so the next screenshare start begins
 * from a clean slate.
 *
 * Background: native screensharing only mutes the desktop track on stop - the
 * sender is kept so the transceiver can be reused - which leaves the desktop
 * MHLocalTrack alive in rtc.localTracks and in state['features/base/tracks']
 * until something else disposes it. A subsequent conference.addTrack() pass
 * (e.g. on hangup-and-rejoin, breakout-room join, or any _conferenceWillJoin
 * path) hands both the camera and the stale desktop to MHConference.addTrack
 * in one synchronous loop, each naming its source from
 * {@link MHConference._getNextSourceIndex} - which reads rtc.localTracks but
 * the new track is only written by _setupNewTrack() a microtask later - so
 * both tracks get source index 0, share one source name, and the bridge
 * publishes whichever videoType it walks last for that name: remote peers see
 * the camera stream on the screenshare tile. Disposing the stale desktop
 * before the synchronous addTrack pass closes that race.
 *
 * The previous implementation used getLocalVideoTrack() (which filters by
 * mediaType VIDEO and matches the camera, not the desktop) and has been
 * returning the camera and disposing nothing since multi-stream landed; this
 * is the same defect fixed on the React Native branch in commit 6360a92de1.
 *
 * @returns {Function}
 */
export function destroyLocalDesktopTrackIfExists() {
    return (dispatch, getState) => {
        const desktopTrack = getLocalDesktopTrack(getState()['features/base/tracks']);

        if (desktopTrack?.mhTrack) {
            // Returned so that callers can await the teardown before creating a
            // replacement track.
            return dispatch(_disposeAndRemoveTracks([ desktopTrack.mhTrack ]));
        }

        return Promise.resolve();
    };
}

/**
 * Sets UID of the displayed no data from source notification. Used to track
 * if the notification was previously displayed in this context.
 *
 * @param {number} uid - Notification UID.
 * @returns {{
    *     type: SET_NO_AUDIO_SIGNAL_UID,
    *     uid: number
    * }}
    */
export function setNoSrcDataNotificationUid(uid) {
    return {
        type: SET_NO_SRC_DATA_NOTIFICATION_UID,
        uid
    };
}

/**
 * Updates the last media event received for a video track.
 *
 * @param {MHRemoteTrack} track - MHTrack instance.
 * @param {string} name - The current media event name for the video.
 * @returns {{
 *     type: TRACK_UPDATE_LAST_VIDEO_MEDIA_EVENT,
 *     track: Track,
 *     name: string
 * }}
 */
export function updateLastTrackVideoMediaEvent(track, name) {
    return {
        type: TRACK_UPDATE_LAST_VIDEO_MEDIA_EVENT,
        track,
        name
    };
}


/**
 * Create an action for when track streaming status changes.
 *
 * @param {(MHRemoteTrack)} track - mhTrack instance.
 * @param {string} streamingStatus - The new streaming status of the track.
 * @returns {{
*     type: TRACK_UPDATED,
*     track: Track
* }}
*/
export function trackStreamingStatusChanged(track, streamingStatus) {
    return {
        type: TRACK_UPDATED,
        track: {
            mhTrack: track,
            streamingStatus
        }
    };
}

/**
 * Add a given local track to the conference.
 *
 * @param {MHLocalTrack} newTrack - The local track to be added to the conference.
 * @returns {Function}
 */
export function addLocalTrack(newTrack) {
    return async (dispatch, getState) => {
        const conference = getCurrentConference(getState());

        if (conference) {
            // //  logger.info('conference');

            await conference.addTrack(newTrack);
        }

        const setMuted = newTrack.isVideoTrack()
            ? newTrack.getVideoType() === VIDEO_TYPE.DESKTOP
                ? setScreenshareMuted
                : setVideoMuted
            : setAudioMuted;
        const isMuted = newTrack.isMuted();

        await dispatch(setMuted(isMuted));

        return dispatch(_addTracks([ newTrack ]));
    };
}

/**
 * Create an action for when a track's muted state change action has failed. This could happen because of
 * {@code getUserMedia} errors during unmute or replace track errors at the peerconnection level.
 *
 * @param {(MHLocalTrack|MHRemoteTrack)} track - JitsiTrack instance.
 * @param {boolean} wasMuting - If the operation that failed was a mute operation or an unmute operation.
 * @returns {{
 *     type: TRACK_MUTE_UNMUTE_FAILED,
 *     track: Track
 * }}
 */
export function trackMuteUnmuteFailed(track: any, wasMuting: boolean) {
    return {
        type: TRACK_MUTE_UNMUTE_FAILED,
        track,
        wasMuting
    };
}
